import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import { useQuery } from "@tanstack/react-query";
import { TransactionsToolbar, StaffActivityTable, ActivityReport } from './components';
import CircularProgress from '@mui/material/CircularProgress';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setTransactions, setTransactionPage } from 'store/actions/transaction';
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "store/actions/snackbarMessage";
import API from 'util/API';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    flexGrow: 1
  },
  content: {
    marginTop: theme.spacing(2)
  },
  uiProgess: {
    position: 'fixed',
    zIndex: '1000',
    height: '31px',
    width: '31px',
    left: '50%',
    top: '35%'
  }
}));

const StaffActivityList = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const selectedTransactionIds = useSelector((state) => state.transaction.ids);
  const orderType = useSelector((state) => state.restaurant.orderType);
  const selectedDate = useSelector((state) => state.restaurant.orderFromDate);
  const setPage = (page) => dispatch(setTransactionPage(page));
  
  const showSuccessMessage = (msg) => dispatch(showSuccessSnackbar(msg));
  const showErrorMessage = (msg) => dispatch(showErrorSnackbar(msg));

  const history = useHistory();

  const getPage = () => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    return params.get('page');
  }

  const fetchStaffActivities = async () => {
    const response = await API.get(
      "/staffActivities?dateStr=" + moment(selectedDate).format("DD-MM-YY")
    );
    
    if (response && response.data && response.data.staffActivityList) {
      return response.data.staffActivityList;
    }
    
    return [];
  };

  const { 
    data: staffActivities = [], 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['staffActivities', moment(selectedDate).format("DD-MM-YY")],
    queryFn: fetchStaffActivities
  });

  // Handle success/error with useEffect instead of callbacks
  useEffect(() => {
    if (staffActivities && !getPage()) {
      setPage(0);
    }
  }, [staffActivities]);

  useEffect(() => {
    if (error) {
      console.log(error);
      if (error.response && error.response.status === 403) {
        history.push("/sign-in");
      }
      setErrorMsg("Error in retrieving the data");
      showErrorMessage("Error in retrieving the data");
    }
  }, [error, history, showErrorMessage]);

  const handleViewChange = (mode) => {
    setViewMode(mode);
  };

  const handleDownloadCSV = () => {
    if (!staffActivities || staffActivities.length === 0) {
      showErrorMessage("No activities to download");
      return;
    }

    try {
      // Define CSV headers
      const headers = [
        'Activity Type',
        'Activity Content',
        'Update Time',
        'Amount',
        'Staff Name',
        'Details'
      ];

      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      
      staffActivities.forEach(activity => {
        const updateTime = activity.updatedAt ? 
          (typeof activity.updatedAt === 'object' && '_seconds' in activity.updatedAt) ?
            moment(new Date(activity.updatedAt._seconds * 1000)).format('DD/MM/YYYY HH:mm:ss') :
            moment(activity.updatedAt).format('DD/MM/YYYY HH:mm:ss') :
          '';
          
        const row = [
          activity.operationType || '',
          activity.operationName || '',
          updateTime,
          `$${activity.price.toFixed(2)}`,
          activity.staffName || '',
          activity.details || ''
        ];
        
        // Escape any commas in the data
        const escapedRow = row.map(field => {
          const escaped = ('' + field).replace(/"/g, '""');
          return `"${escaped}"`;
        });
        
        csvContent += escapedRow.join(',') + '\n';
      });

      // Create blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.setAttribute('href', url);
      link.setAttribute('download', `staff-activities-${moment(selectedDate).format('DD-MM-YYYY')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccessMessage("CSV downloaded successfully");
    } catch (error) {
      console.error("Error generating CSV:", error);
      showErrorMessage("Error generating CSV file");
    }
  };

  if (isLoading) {
    return (
      <main className={classes.content}>
        <CircularProgress size={150} className={classes.uiProgess} />
      </main>
    );
  } else {
    return (
      <div className={classes.root}>
        <TransactionsToolbar 
          viewMode={viewMode} 
          onViewChange={handleViewChange}
          onDownloadCSV={handleDownloadCSV}
        />
        <div className={classes.content}>
          {viewMode === 'table' ? (
            <StaffActivityTable staffActivities={staffActivities} />
          ) : (
            <ActivityReport staffActivities={staffActivities} />
          )}
        </div>
      </div>
    );
  }
};

export default StaffActivityList;

